import { cn } from '../lib/utils';

/**
 * Los tramos de una fila: celdas contiguas del mismo contenido, en el orden en
 * que se ensartan.
 *
 * Es presentacional puro. Recibe un color ya resuelto, un número y dos textos
 * ya traducidos, y no sabe qué es una paleta, una fila ni un patrón
 * (Artículo II.4): quien lo monta calcula los tramos con el dominio.
 *
 * El `ordinal` es lo que distingue dos tramos sin recurrir al color: la paleta
 * no tiene nombres —RF-14 de 001 los prohíbe—, pero sí posiciones, y un número
 * es una segunda señal que sobrevive a cualquier daltonismo.
 */
export interface BeadRun {
  /** Color de la mostacilla, o `null` si el tramo es un hueco. */
  readonly color: string | null;
  readonly count: number;
  /** Texto accesible ya traducido: «3 × color 2», «2 sin mostacilla». */
  readonly label: string;
  /** Posición en la paleta, ya formateada. Ausente en los huecos. */
  readonly ordinal?: string;
}

export interface BeadRunListProps {
  readonly runs: readonly BeadRun[];
  /** Nombre accesible de la lista, ya traducido. */
  readonly label: string;
  readonly className?: string;
}

export function BeadRunList({ runs, label, className }: BeadRunListProps) {
  return (
    <ul
      data-slot="bead-run-list"
      aria-label={label}
      className={cn('flex flex-wrap items-center gap-2', className)}
    >
      {runs.map((run, index) => {
        const gap = run.color === null;

        return (
          <li
            // Los tramos no tienen identidad propia: dos iguales en la misma
            // fila son indistinguibles, y la posición es lo único estable.
            key={`${index}-${run.color ?? 'gap'}`}
            data-slot="bead-run"
            data-gap={gap}
            aria-label={run.label}
            className={cn(
              'flex min-h-11 items-center gap-1.5 rounded-md border px-2 py-1',
              gap ? 'border-muted-foreground/60 border-dashed' : 'border-border',
            )}
          >
            <span
              data-slot="bead-run-swatch"
              aria-hidden="true"
              // El hueco se dibuja como ausencia: sin relleno y con el borde
              // punteado, para que no se lea como un color más de la paleta.
              className={cn(
                'size-6 shrink-0 rounded-full border',
                gap && 'border-muted-foreground/60 border-dashed bg-transparent',
              )}
              style={gap ? undefined : { backgroundColor: run.color ?? undefined }}
            />
            <span data-slot="bead-run-count" className="text-base font-semibold tabular-nums">
              {run.count}
            </span>
            {run.ordinal !== undefined && (
              <span
                data-slot="bead-run-ordinal"
                aria-hidden="true"
                className="text-muted-foreground text-xs tabular-nums"
              >
                {run.ordinal}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
